import { startTransition, useMemo, useState } from 'react';

import { Menubar } from 'primereact/menubar';
import { MenuItem } from 'primereact/menuitem';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import { RecaneEmbryosDialog } from '@/components/common/recane-embryos-dialog';
import { RecanePrintWizardDialog } from '@/components/common/recane-print-wizard-dialog';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useFreezePdfPrint } from '@/components/hooks/useFreezePdfPrint';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import { useDairyCompPulseExportReport } from '@/services/reports';
import { type ShipmentEmbryo } from '@/services/shipping/types';

import { useShipmentContext } from '../../hooks/useShipmentContext';
import { AbcFormDReportDialog } from '../AbcFormDReportDialog';

export function getExistingCaneCodes(
  embryos: ShipmentEmbryo[],
  occupiedCaneCodes: string[] = [],
) {
  return Array.from(
    new Set(
      [...embryos.map((embryo) => embryo.cane), ...occupiedCaneCodes]
        .map((cane) => cane?.trim())
        .filter((cane): cane is string => Boolean(cane)),
    ),
  );
}

export function useEmbryosMenubarItems({
  embryos,
  selectedEmbryos,
  isBulkEditEnabled,
  openBulkEditDialog,
  setRecaneEmbryosDialogVisible,
  setRecanePrintWizardVisible,
  exportDairyComp,
  printPdf,
  setAbcFormType,
  executeRemoveFromShipmentWorkflow,
  canExecuteWorkflow,
  isRemoveFromShipmentDisabled,
}: {
  embryos: ShipmentEmbryo[];
  selectedEmbryos: ShipmentEmbryo[];
  isBulkEditEnabled?: boolean;
  openBulkEditDialog?: () => void;
  refetchEmbryos: () => Promise<any>;
  refetchShipment: () => Promise<any>;
  setRecaneEmbryosDialogVisible: (visible: boolean) => void;
  setRecanePrintWizardVisible?: (visible: boolean) => void;
  setAbcFormType: (type: 'pdf' | 'excel' | undefined) => void;
  exportDairyComp: (args: { embryoIds: string[] }) => Promise<any>;
  printPdf: (labelType: 'GobletLabel' | 'StrawLabel' | 'CaneTabLabel') => void;
  executeRemoveFromShipmentWorkflow: (
    embryos: ShipmentEmbryo[],
  ) => Promise<void>;
  canExecuteWorkflow: boolean;
  isRemoveFromShipmentDisabled: boolean;
}): MenuItem[] {
  const isRecanePrintWizardEnabled = useFeatureFlag(
    'Print_cane_goblet_efficiency',
  );

  return useMemo<MenuItem[]>(() => {
    const menuItems: MenuItem[] = [];

    if (isBulkEditEnabled && openBulkEditDialog) {
      menuItems.push({
        label: 'Edit Embryos',
        disabled: selectedEmbryos.length === 0,
        command() {
          openBulkEditDialog();
        },
      });
    }

    menuItems.push(
      isRecanePrintWizardEnabled && setRecanePrintWizardVisible
        ? {
            label: 'Re-cane & Print',
            command() {
              setRecanePrintWizardVisible(true);
            },
          }
        : {
            label: 'Re-cane Embryos',
            command() {
              setRecaneEmbryosDialogVisible(true);
            },
          },
      {
        label: 'ABC Form Part D',
        items: [
          {
            label: 'PDF',
            disabled: !selectedEmbryos.length,
            command() {
              setAbcFormType('pdf');
            },
          },
          {
            label: 'Excel',
            disabled: !selectedEmbryos.length,
            command() {
              setAbcFormType('excel');
            },
          },
        ],
      },
      {
        label: 'Export DC Pulse Data',
        disabled: !embryos?.length,
        command() {
          if (embryos?.length) {
            exportDairyComp({
              embryoIds: embryos.map((e) => e.id),
            });
          }
        },
      },
      {
        label: 'Workflows',
        icon: 'pi pi-wrench',
        disabled: !canExecuteWorkflow || isRemoveFromShipmentDisabled,
        items: [
          {
            label: `Remove from Shipment`,
            command: () => {
              executeRemoveFromShipmentWorkflow(selectedEmbryos);
            },
            disabled: !selectedEmbryos.length || isRemoveFromShipmentDisabled,
          },
        ],
      },
      {
        label: 'Print Options',
        icon: 'pi pi-print',
        items: [
          {
            label: `Goblet`,
            command: () => {
              printPdf('GobletLabel');
            },
            disabled: !selectedEmbryos.length,
          },
          {
            label: 'Straw',
            command: () => {
              printPdf('StrawLabel');
            },
            disabled: !selectedEmbryos.length,
          },
          {
            label: 'Cane Tab',
            command: () => {
              printPdf('CaneTabLabel');
            },
            disabled: !selectedEmbryos.length,
          },
        ],
      },
    );

    return menuItems;
  }, [
    embryos,
    exportDairyComp,
    isBulkEditEnabled,
    openBulkEditDialog,
    printPdf,
    selectedEmbryos,
    setAbcFormType,
    setRecaneEmbryosDialogVisible,
    setRecanePrintWizardVisible,
    isRecanePrintWizardEnabled,
    executeRemoveFromShipmentWorkflow,
    canExecuteWorkflow,
    isRemoveFromShipmentDisabled,
  ]);
}

export function EmbryosMenubar({
  selectedEmbryos,
  occupiedCaneCodes,
  isBulkEditEnabled,
  openBulkEditDialog,
  executeRemoveFromShipmentWorkflow,
  canExecuteWorkflow,
  refetchEmbryos,
  refetchShipment,
}: {
  selectedEmbryos: ShipmentEmbryo[];
  occupiedCaneCodes?: string[];
  isBulkEditEnabled?: boolean;
  openBulkEditDialog?: () => void;
  executeRemoveFromShipmentWorkflow: (
    embryos: ShipmentEmbryo[],
  ) => Promise<void>;
  canExecuteWorkflow: boolean;
  refetchEmbryos: () => Promise<any>;
  refetchShipment: () => Promise<any>;
}) {
  const { embryos, shipment } = useShipmentContext();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isUniqueCaneEnabled = useFeatureFlag('Recane_Unique_Cane_Suggestion');
  const isRecanePrintWizardEnabled = useFeatureFlag(
    'Print_cane_goblet_efficiency',
  );

  const LOCKED_SHIPMENT_STATUSES = [
    ShipmentStatus.IN_PROGRESS as number,
    ShipmentStatus.SHIPPED as number,
  ];

  const isRemoveFromShipmentDisabled = useMemo(() => {
    if (!shipment?.shipmentStatus) return false;
    return LOCKED_SHIPMENT_STATUSES.includes(shipment.shipmentStatus);
  }, [shipment?.shipmentStatus]);

  const allExistingCaneCodes = useMemo(
    () => getExistingCaneCodes(embryos, occupiedCaneCodes),
    [embryos, occupiedCaneCodes],
  );

  const [recaneEmbryosDialogVisible, setRecaneEmbryosDialogVisible] =
    useState(false);
  const [recanePrintWizardVisible, setRecanePrintWizardVisible] =
    useState(false);
  const { printPdf } = useFreezePdfPrint({
    getEmbryoData: () => ({
      selectedTempEmbryos: [],
      selectedLiveEmbryos: selectedEmbryos ?? [],
      fertilizations: [],
    }),
    onComplete: () => {},
  });

  const [abcFormType, setAbcFormType] = useState<'pdf' | 'excel'>();

  const [exportDairyComp] = useDairyCompPulseExportReport();

  const embryosMenu = useEmbryosMenubarItems({
    embryos,
    selectedEmbryos,
    isBulkEditEnabled,
    openBulkEditDialog,
    refetchEmbryos,
    refetchShipment,
    setRecaneEmbryosDialogVisible,
    setRecanePrintWizardVisible,
    exportDairyComp,
    printPdf,
    setAbcFormType,
    executeRemoveFromShipmentWorkflow,
    canExecuteWorkflow,
    isRemoveFromShipmentDisabled,
  });

  return (
    <>
      <RecaneEmbryosDialog
        visible={recaneEmbryosDialogVisible}
        embryos={selectedEmbryos}
        existingCaneCodes={
          isUniqueCaneEnabled ? allExistingCaneCodes : undefined
        }
        onHide={() => {
          setRecaneEmbryosDialogVisible(false);
        }}
        onSuccess={async () => {
          dispatch(
            addToast({
              severity: 'success',
              summary: t('common.success'),
              detail: t('embryo.recaneSuccess'),
              life: ToastLife.MEDIUM,
            }),
          );
          setRecaneEmbryosDialogVisible(false);
          startTransition(() => {
            refetchEmbryos();
            refetchShipment();
          });
        }}
        onError={(error) => {
          const message =
            error instanceof Error ? error.message : t('common.unknownError');
          dispatch(
            addToast({
              severity: 'error',
              summary: t('common.error'),
              detail: message,
              life: ToastLife.LONG,
            }),
          );
          setRecaneEmbryosDialogVisible(false);
        }}
      />

      {isRecanePrintWizardEnabled && (
        <RecanePrintWizardDialog
          visible={recanePrintWizardVisible}
          embryos={selectedEmbryos}
          existingCaneCodes={
            isUniqueCaneEnabled ? allExistingCaneCodes : undefined
          }
          onHide={() => {
            setRecanePrintWizardVisible(false);
          }}
          onSuccess={async () => {
            dispatch(
              addToast({
                severity: 'success',
                summary: t('common.success'),
                detail: t('embryo.recanePrintSuccess'),
                life: ToastLife.MEDIUM,
              }),
            );
            setRecanePrintWizardVisible(false);
            startTransition(() => {
              refetchEmbryos();
              refetchShipment();
            });
          }}
          onError={(error) => {
            const message =
              error instanceof Error ? error.message : t('common.unknownError');
            dispatch(
              addToast({
                severity: 'error',
                summary: t('common.error'),
                detail: message,
                life: ToastLife.LONG,
              }),
            );
          }}
        />
      )}

      <AbcFormDReportDialog
        selectedEmbryos={selectedEmbryos}
        type={abcFormType}
        onClose={() => setAbcFormType(undefined)}
      />

      <Menubar model={embryosMenu} />
    </>
  );
}
